import { ChevronRight } from 'lucide-react';
import { ReactNode, useState } from 'react';
import styled from 'styled-components';

interface Props {
  title: string;
  defaultOpen?: boolean;
  children: ReactNode;
  testId?: string;
}

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  overflow: hidden;
`;

const Header = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.bgMuted};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`;

const Caret = styled(ChevronRight)<{ $open: boolean }>`
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform ${({ theme }) => theme.transitions.fast};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Body = styled.div`
  padding: 4px 16px 16px;
`;

export const CollapsibleSection = ({
  title,
  defaultOpen = false,
  children,
  testId,
}: Props) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Wrapper data-testid={testId}>
      <Header
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span>{title}</span>
        <Caret size={16} $open={open} />
      </Header>
      {open && <Body>{children}</Body>}
    </Wrapper>
  );
};
